import { ShimmerText } from '@/components/feedback/shimmer-text';
import { useAnalytics } from '@/services/analytics';
import { useServicesMutation } from '@/services/api';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { getCurrentRegion, Region } from '@/utils/environment';
import { copyToClipboard } from '@/hooks/user-actions/use-copy-to-clipboard';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import type { CopilotSession, CopilotStatefulMessage } from '@customerio/hydra-copilot';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  BannerDescription,
  Box,
  Button,
  CodeBlock,
  Form,
  FormLayout,
  Icon,
  ButtonGroup,
  Flex,
  InlineStack,
  Link,
  PlainButton,
  Popover,
  PopoverContent,
  Stack,
  Text,
  TextArea,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { m } from 'motion/react';
import plur from 'plur';
import type { FormEvent } from 'react';
import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Attachments, type FileData } from '../attachments';
import { Markdown } from '../primitives/markdown';
import { MessageAction, MessageActionsContainer, MessageContainer } from '../primitives/message-container';
import { References, type Reference } from '../references';
import { AnimateMessage } from './animate-message';
import { ToolCalls } from './tool-calls';

type ErrorMessageProps = { error: string; errorDetails?: string };

function isRateLimitError(error: string, errorDetails?: string): boolean {
  const haystack = `${error} ${errorDetails ?? ''}`.toLowerCase();
  return haystack.includes('429') || haystack.includes('rate limit');
}

function RateLimitHintBanner() {
  const useGlobalVertex = useFeatureFlag(AccountFeatureFlag.UseGlobalVertexAi);
  const isEu = getCurrentRegion() === Region.EU;
  if (!isEu || useGlobalVertex === true) {
    return null;
  }
  return (
    <Banner
      variant="warning"
      title="EU endpoint has lower capacity"
      description={
        <>
          You're using the EU endpoint, which has lower capacity and is more likely to hit rate limits. If you're
          comfortable with your data being processed in other regions, you can enable the global endpoint for higher
          limits, more models, and better performance in{' '}
          <Link href="/settings/ai" isUnderlined>
            AI &amp; MCP settings
          </Link>
          .
        </>
      }
    />
  );
}

function ErrorMessage({ error, errorDetails }: ErrorMessageProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Stack gap="100">
      <Banner variant="error" title="Error">
        <BannerDescription>{error}</BannerDescription>
        {!isEmptyString(errorDetails) ? (
          <Stack gap="075">
            <PlainButton
              display="flex"
              alignItems="center"
              gap="025"
              onClick={() => setIsOpen((previous) => !previous)}
              aria-expanded={isOpen}
            >
              <Text size="xs" color="subtle">
                {isOpen ? 'Hide details' : 'Show details'}
              </Text>
              <m.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon name="chevron-right" size="xs" color="subtle" />
              </m.div>
            </PlainButton>
            {isOpen ? <CodeBlock code={errorDetails} language="text" mode="dark" /> : null}
          </Stack>
        ) : null}
      </Banner>
      {isRateLimitError(error, errorDetails) ? <RateLimitHintBanner /> : null}
    </Stack>
  );
}

type CompactionMessageProps = { status: 'compacting' | 'complete'; summary?: string };

function CompactionMessage({ status, summary }: CompactionMessageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const hasSummary = summary != null && summary !== '';
  const isExpandable = status === 'complete' && hasSummary;

  useEffect(() => {
    if (status !== 'compacting') {
      setProgress(100);
      return;
    }

    // Animate progress with diminishing increments (never reaches 100 until complete)
    const interval = setInterval(() => {
      setProgress((previous) => {
        const remaining = 90 - previous;
        return previous + remaining * 0.015;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [status]);

  return (
    <AnimateMessage>
      <Box display="flex" flexDirection="column" gap="050" py="100">
        {status === 'compacting' ? (
          <>
            <Text size="s" color="subtle" fontStyle="italic">
              Compacting our conversation so we can keep chatting…
            </Text>
            <Box display="flex" alignItems="center" gap="100">
              <Box
                flexGrow="1"
                style={{
                  height: '4px',
                  borderRadius: themeVars.radius['full'],
                  backgroundColor: themeVars.color['surface-active'],
                  overflow: 'hidden',
                }}
              >
                <Box
                  h="full"
                  style={{
                    width: `${Math.round(progress)}%`,
                    borderRadius: themeVars.radius['full'],
                    backgroundColor: themeVars.color['surface-bold'],
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Text size="xs" color="subtle">
                {Math.round(progress)}%
              </Text>
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap="050"
            cursor={isExpandable ? 'pointer' : undefined}
            onClick={isExpandable ? () => setIsOpen((previous) => !previous) : undefined}
          >
            {isExpandable ? (
              isOpen ? (
                <Icon name="chevron-down" size="sm" />
              ) : (
                <Icon name="chevron-right" size="sm" />
              )
            ) : null}
            <Icon color="success" name="check-circle" size="sm" />
            <Text size="xs" color="subtle">
              Conversation compacted
            </Text>
          </Box>
        )}
        {isOpen && hasSummary ? (
          <Box px="100" pt="050">
            <Markdown>{summary}</Markdown>
          </Box>
        ) : null}
      </Box>
    </AnimateMessage>
  );
}

type ThoughtMessageProps = {
  message: CopilotStatefulMessage & { type: 'text'; thought?: boolean };
  isStreaming?: boolean;
};

function ThoughtMessage({ message, isStreaming }: ThoughtMessageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wasStreamingRef = useRef(false);

  const durationSeconds = 'duration' in message && typeof message.duration === 'number' ? message.duration : null;

  const formattedDuration = (() => {
    if (durationSeconds == null) return null;

    if (durationSeconds < 60) {
      return `${durationSeconds} ${plur('second', durationSeconds)}`;
    } else {
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = durationSeconds % 60;
      if (seconds === 0) {
        return `${minutes} ${plur('minute', minutes)}`;
      }
      return `${minutes} ${plur('minute', minutes)} ${seconds} ${plur('second', seconds)}`;
    }
  })();

  // Auto-open when streaming starts, auto-close when streaming ends
  useEffect(() => {
    if (isStreaming === true) {
      wasStreamingRef.current = true;
      setIsOpen(true);
    } else if (wasStreamingRef.current) {
      wasStreamingRef.current = false;
      setIsOpen(false);
    }
  }, [isStreaming]);

  return (
    <AnimateMessage>
      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          alignItems="center"
          gap="075"
          py="075"
          cursor="pointer"
          onClick={() => setIsOpen((previous) => !previous)}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen((previous) => !previous);
            }
          }}
        >
          <Text as={formattedDuration == null ? ShimmerText : 'span'} color="subtle" size="s">
            {formattedDuration != null ? `Thought for ${formattedDuration}` : 'Thinking\u2026'}
          </Text>
          <m.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon name="chevron-down" size="xs" color="subtle" />
          </m.div>
        </Box>
        <m.div
          initial={false}
          animate={{
            height: isOpen ? 'auto' : 0,
            opacity: isOpen ? 1 : 0,
          }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <Box py="075">
            <Markdown isAnimating={isStreaming} color="subtle" size="s">
              {message.text}
            </Markdown>
          </Box>
        </m.div>
      </Box>
    </AnimateMessage>
  );
}

type MessageProps = {
  message: CopilotStatefulMessage;
  session: CopilotSession;
  isLastMessage?: boolean;
  isStreaming?: boolean;
};

export function getMessageKey(message: CopilotStatefulMessage, index: number) {
  // We do not get stable backend IDs for messages, so this uses the row's
  // append-order identity while avoiding fields like timestamp that change as
  // streaming and compaction messages settle.
  const messageType = message.role === 'model' ? message.type : 'user';
  return `${message.role}:${messageType}:${index}`;
}

function MessageComponent({ message, session, isLastMessage = false, isStreaming = false }: MessageProps) {
  const auth = useAuth();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const [feedbackReasonOpen, setFeedbackReasonOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');

  const submitFeedbackMutation = useServicesMutation('recordCopilotFeedback', {
    onSuccess: (_, variables) => {
      snackbars.create({
        message: 'Thank you for your feedback!',
      });

      if (message.role !== 'model' || message.type !== 'text' || message.meta?.id == null) {
        return;
      }

      session.setMessageMeta(message.meta.id, {
        ...message.meta,
        thumbs_up: variables.body?.thumbs_up,
        thumbs_down: variables.body?.thumbs_down,
      });

      setFeedbackReasonOpen(false);
      setFeedbackReason('');
    },
    onError: (error) => {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to submit feedback. Please try again.',
        variant: 'error',
      });
    },
  });

  const submitThumbsDown = useCallback(
    (reason?: string) => {
      if (message.role !== 'model' || message.type !== 'text' || message.meta?.id == null) {
        return;
      }

      submitFeedbackMutation.mutate({
        params: {
          accountId: auth.account.id,
          sessionId: Number(session.id),
          logId: message.meta.id,
        },
        body: {
          thumbs_down: true,
          ...(!isEmptyString(reason) ? { feedback_reason: reason } : {}),
        },
      });
      track('aiAssistant', 'thumbsDown', {
        'Feedback Text': reason ?? '',
        'Has Feedback': !isEmptyString(reason),
      });
    },
    [auth.account.id, message, session.id, submitFeedbackMutation, track],
  );

  const fileChips = useMemo(() => {
    if (message.role !== 'user') return [];

    return message.attachments.map(
      (attachment, index) =>
        ({
          id: `readonly-${index}`,
          name: attachment.display_name ?? 'File',
          mimeType: attachment.mime_type ?? 'application/octet-stream',
          source: attachment.content,
        } as FileData),
    );
  }, [message]);

  if (message.role === 'user') {
    const textContent = !isEmptyString(message.text) ? (
      <Markdown>{message.text}</Markdown>
    ) : (
      <Text as="p" size="s" color="subtle" fontStyle="italic">
        No message
      </Text>
    );

    return (
      <Box display="flex" flexDirection="column" gap="100">
        {fileChips.length > 0 ? (
          <Box display="flex" justifyContent="flex-end">
            <Attachments files={fileChips} session={session} variant="grid" />
          </Box>
        ) : null}
        <MessageContainer variant="user">
          <Box display="flex" flexDirection="column" gap="100">
            {textContent}
          </Box>
        </MessageContainer>
      </Box>
    );
  } else if (message.role === 'model' && message.type === 'text') {
    const sources = ('meta' in message && message.meta?.sources != null ? message.meta.sources : []) as Reference[];
    const otherSources = sources.filter((source) => source?.type !== 'business_profile');

    const hasMetaId = 'meta' in message && message.meta?.id != null;
    const hasThumbsUp = 'meta' in message && message.meta?.thumbs_up === true;
    const hasThumbsDown = 'meta' in message && message.meta?.thumbs_down === true;
    const isThought = 'thought' in message && message.thought === true;
    const showReferences = otherSources.length > 0;

    if (isThought) {
      return <ThoughtMessage message={message} isStreaming={isStreaming} />;
    }

    return (
      <AnimateMessage>
        <MessageContainer variant="model">
          <Markdown isAnimating={isStreaming}>{message.text}</Markdown>
          {hasMetaId || showReferences ? (
            <Box>
              <MessageActionsContainer isVisible={isLastMessage} alignBlock="center" alignInline="space-between">
                {hasMetaId ? (
                  <InlineStack gap="050" alignBlock="center">
                    <MessageAction
                      tooltip="Copy to clipboard"
                      onClick={async () => {
                        const success = await copyToClipboard(message.text);
                        if (success) {
                          snackbars.create({
                            message: 'Copied to clipboard',
                          });
                          track('aiAssistant', 'messageCopied', {});
                        }
                      }}
                      aria-label="Copy to clipboard"
                    >
                      <Icon name="copy-to-clipboard" size="xs" />
                    </MessageAction>
                    {!hasThumbsDown ? (
                      <MessageAction
                        tooltip="This was helpful"
                        variant={hasThumbsUp ? 'active' : 'default'}
                        onClick={
                          hasThumbsUp
                            ? undefined
                            : () => {
                                submitFeedbackMutation.mutate({
                                  params: {
                                    accountId: auth.account.id,
                                    sessionId: Number(session.id),
                                    logId: message.meta.id,
                                  },
                                  body: {
                                    thumbs_up: true,
                                  },
                                });
                                track('aiAssistant', 'thumbsUp', {});
                              }
                        }
                        aria-label="Thumbs up"
                      >
                        <Icon name="thumbs-up" size="xs" />
                      </MessageAction>
                    ) : null}
                    {!hasThumbsUp ? (
                      <Popover
                        placement="bottom-start"
                        withArrow={false}
                        isOpen={feedbackReasonOpen}
                        shouldTriggerOnClick={!hasThumbsDown}
                        onOpenChange={(open) => {
                          if (!open && !hasThumbsDown && !submitFeedbackMutation.isPending) {
                            submitThumbsDown(feedbackReason);
                          }
                          setFeedbackReasonOpen(open);
                          if (!open) {
                            setFeedbackReason('');
                          }
                        }}
                      >
                        {(context) => (
                          <MessageAction
                            ref={context.reference.setReference}
                            {...context.reference.getReferenceProps()}
                            tooltip={feedbackReasonOpen ? undefined : 'This was not helpful'}
                            variant={hasThumbsDown ? 'active' : 'default'}
                            aria-label="Thumbs down"
                          >
                            <Icon name="thumbs-down" size="xs" />
                          </MessageAction>
                        )}
                        <PopoverContent>
                          <Form
                            onSubmit={(event: FormEvent) => {
                              event.preventDefault();
                              submitThumbsDown(feedbackReason);
                            }}
                          >
                            <FormLayout>
                              <Text size="s" weight="semibold">
                                What went wrong?
                              </Text>
                              <TextArea
                                ariaLabel="Feedback reason"
                                placeholder="Tell us what could be improved..."
                                value={feedbackReason}
                                onInput={(event) => {
                                  setFeedbackReason(event.target.value);
                                }}
                                rows={3}
                                style={{ minWidth: '260px' }}
                              />
                              <Flex justifyContent="end">
                                <ButtonGroup groupVariant="spaced">
                                  <Button
                                    variant="subtle"
                                    size="sm"
                                    type="button"
                                    isDisabled={submitFeedbackMutation.isPending}
                                    onClick={() => {
                                      submitThumbsDown();
                                    }}
                                  >
                                    Skip
                                  </Button>
                                  <Button
                                    variant="primary"
                                    type="submit"
                                    size="sm"
                                    isLoading={submitFeedbackMutation.isPending}
                                  >
                                    Submit
                                  </Button>
                                </ButtonGroup>
                              </Flex>
                            </FormLayout>
                          </Form>
                        </PopoverContent>
                      </Popover>
                    ) : null}
                  </InlineStack>
                ) : (
                  <div />
                )}
                {showReferences ? (
                  <InlineStack gap="100" alignBlock="center">
                    <References references={otherSources} />
                  </InlineStack>
                ) : null}
              </MessageActionsContainer>
            </Box>
          ) : null}
        </MessageContainer>
      </AnimateMessage>
    );
  } else if (message.role === 'model' && message.type === 'tool_call') {
    return <ToolCalls message={message} session={session} />;
  } else if (message.role === 'model' && message.type === 'compaction') {
    return <CompactionMessage status={message.status} summary={message.summary} />;
  } else if (message.role === 'model' && message.type === 'error') {
    return (
      <AnimateMessage>
        <ErrorMessage error={message.error} errorDetails={message.error_details} />
      </AnimateMessage>
    );
  } else {
    // typescript hack to ensure all cases are handled
    const never: never = message;
    // eslint-disable-next-line no-console
    console.error('Unknown message type', never);
    return null;
  }
}

function areMessagePropsEqual(previous: MessageProps, next: MessageProps) {
  // statefulMessages creates fresh objects for unchanged rows, so reference
  // equality alone would still rerender long chat history on each backend update.
  return (
    previous.session === next.session &&
    (previous.isLastMessage ?? false) === (next.isLastMessage ?? false) &&
    (previous.isStreaming ?? false) === (next.isStreaming ?? false) &&
    areStatefulMessagesEqual(previous.message, next.message)
  );
}

function areStatefulMessagesEqual(previous: CopilotStatefulMessage, next: CopilotStatefulMessage) {
  if (previous === next) {
    return true;
  }

  if (previous.role !== next.role || previous.type !== next.type || previous.timestamp !== next.timestamp) {
    return false;
  }

  if (previous.role === 'user') {
    if (next.role !== 'user') {
      return false;
    }

    return previous.text === next.text && areAttachmentsEqual(previous.attachments, next.attachments);
  }

  if (next.role !== 'model') {
    return false;
  }

  switch (previous.type) {
    case 'text':
      if (next.type !== 'text') {
        return false;
      }

      return (
        previous.text === next.text &&
        previous.thought === next.thought &&
        previous.duration === next.duration &&
        getMetaSignature(previous.meta) === getMetaSignature(next.meta)
      );
    case 'tool_call':
      if (next.type !== 'tool_call') {
        return false;
      }

      return getToolCallsSignature(previous.tool_calls) === getToolCallsSignature(next.tool_calls);
    case 'compaction':
      if (next.type !== 'compaction') {
        return false;
      }

      return previous.status === next.status && previous.summary === next.summary;
    case 'error':
      if (next.type !== 'error') {
        return false;
      }

      return previous.error === next.error && previous.error_details === next.error_details;
    default:
      return false;
  }
}

function areAttachmentsEqual(
  previous: Extract<CopilotStatefulMessage, { role: 'user' }>['attachments'],
  next: Extract<CopilotStatefulMessage, { role: 'user' }>['attachments'],
) {
  if (previous.length !== next.length) {
    return false;
  }

  return previous.every((attachment, index) => {
    const nextAttachment = next[index];
    return (
      attachment.content === nextAttachment?.content &&
      attachment.mime_type === nextAttachment.mime_type &&
      attachment.display_name === nextAttachment.display_name
    );
  });
}

function getMetaSignature(meta: Extract<CopilotStatefulMessage, { role: 'model'; type: 'text' }>['meta']) {
  return JSON.stringify(meta);
}

function getToolCallsSignature(
  toolCalls: Extract<CopilotStatefulMessage, { role: 'model'; type: 'tool_call' }>['tool_calls'],
) {
  return JSON.stringify(
    toolCalls.map((toolCall) => {
      // respond is a callback recreated during stateful-message derivation; the
      // UI only cares whether a response affordance exists, not the function identity.
      const { respond: _respond, ...serializableToolCall } = toolCall;
      return {
        ...serializableToolCall,
        hasRespond: _respond != null,
      };
    }),
  );
}

export const Message = memo(MessageComponent, areMessagePropsEqual);
Message.displayName = 'Message';
